import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { API } from "../../config";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

interface GalleryItem {
  gallery_id: number;
  title: string | null;
  media_type: string;
  media_url: string | null;
  thumbnail_url: string | null;
  display_order: number;
  record_status_id: number;
}

function getEmbedUrl(url: string): string {
  const ytsMatch = url.match(/youtube\.com\/shorts\/([A-Za-z0-9_-]+)/);
  if (ytsMatch) return `https://www.youtube-nocookie.com/embed/${ytsMatch[1]}`;
  const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([A-Za-z0-9_-]+)/);
  if (ytMatch) return `https://www.youtube-nocookie.com/embed/${ytMatch[1]}`;
  return url;
}

export const GalleryBox = () => {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    fetch(`${API}/gallery`)
      .then((res) => res.json())
      .then((data) => {
        const active = (data.items as GalleryItem[]).filter(
          (g) => g.record_status_id === 1 && g.media_type !== "video(9:16)" && g.media_type !== "reel" && g.media_type !== "youtube(9:16)"
        );
        active.sort((a, b) => a.display_order - b.display_order);
        setItems(active);
      })
      .catch((err) => console.error("Failed to load gallery:", err));
  }, []);

  const nextItem = () => setIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));
  const prevItem = () => setIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1));

  const isVideo = (item: GalleryItem) =>
    item.media_type === "video" || item.media_type === "youtube" || item.media_type === "reel";

  const getThumb = (item: GalleryItem) => item.thumbnail_url || item.media_url || "";

  return (
    <section className="relative pt-60 bg-violet-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="heading-bold headingcolor mb-4">Gallery</h2>
          <p className="subheading text-center max-w-[698px] mx-auto">
            Take a glimpse into our classrooms and learning spaces. From phonics to confident communication, discover how students learn, participate, and grow with us.
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="wrapper px-55 relative">
          <Swiper
            modules={[Autoplay, Pagination, Navigation]}
            className="testimonialsSwiper"
            spaceBetween={30}
            loop
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            pagination={{ el: ".gallery-pagination", clickable: true }}
            navigation={{
              nextEl: ".gallery-next",
              prevEl: ".gallery-prev",
            }}
            slidesPerView={5}
            breakpoints={{
              320: { slidesPerView: 1, spaceBetween: 15 },
              640: { slidesPerView: 1, spaceBetween: 20 },
              768: { slidesPerView: 3, spaceBetween: 25 },
              1024: { slidesPerView: 5, spaceBetween: 30 },
              1280: { slidesPerView: 5, spaceBetween: 30 },
            }}
          >
            {items.map((item, i) => (
              <SwiperSlide key={item.gallery_id}>
                <div
                  onClick={() => {
                    setIndex(i);
                    setOpen(true);
                  }}
                  className="cursor-pointer relative"
                >
                  {isVideo(item) ? (
                    <div className="relative">
                      <img
                        src={getThumb(item)}
                        className="w-full h-[342px] md:h-[202px] object-cover rounded-lg"
                        alt={item.title ?? "Phonics Power gallery"}
                        width={300}
                        height={202}
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-white px-3 py-2 rounded-full shadow text-xl">
                          ▶
                        </div>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={item.media_url ?? ""}
                      className="w-full h-[342px] md:h-[202px] object-cover rounded-lg"
                      alt={item.title ?? "Phonics Power gallery"}
                      width={300}
                      height={202}
                      loading="lazy"
                      decoding="async"
                    />
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          <div className="gallery-prev">
            <b>❮</b>
          </div>

          <div className="gallery-next">
            <b>❯</b>
          </div>
          <div className="gallery-pagination mt-4 text-center"></div>
        </div>

        {/* Popup */}
        {open && items[index] && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
            <div className="relative w-[90%] md:w-[800px]">
              <button
                onClick={() => setOpen(false)}
                className="absolute -top-10 right-0 text-white text-2xl"
                aria-label="Close gallery"
              >
                <span aria-hidden="true">✕</span>
              </button>
              <button
                onClick={prevItem}
                className="absolute left-[-50px] top-1/2 text-white text-3xl"
                aria-label="Previous image"
              >
                <span aria-hidden="true">❮</span>
              </button>
              <button
                onClick={nextItem}
                className="absolute right-[-50px] top-1/2 text-white text-3xl"
                aria-label="Next image"
              >
                <span aria-hidden="true">❯</span>
              </button>

              {isVideo(items[index]) ? (
                <iframe
                  width="100%"
                  height="450"
                  src={getEmbedUrl(items[index].media_url ?? "")}
                  allowFullScreen
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                  className="rounded-lg"
                />
              ) : (
                <img
                  src={items[index].media_url ?? ""}
                  className="w-full rounded-lg"
                  alt={items[index].title ?? "Phonics Power gallery"}
                  decoding="async"
                />
              )}
            </div>
          </div>
        )}

        <div className="text-center mt-6">
          <p className="subheading text-center max-w-[698px] mx-auto">
            Join thousands of happy families who have chosen Phonics Power for their children's educational journey.
          </p>
          <div className="inline-flex justify-center pt-5">
            <Link
              to="/centres"
              className="w-40 h-10 flex items-center justify-center bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm font-semibold font-['Nunito'] leading-5 transition"
            >
              Visit Our Centres
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
